/**
 * Trend keys are dotted paths under `series.generic_data` of a test's
 * rawOutput (e.g. ['materials', 'water', 'mean']). The control limits for a
 * trend are found through the `limitKey` that the evaluation registered for
 * the same measurement -- which is not always the trend key. The GE Optima 540
 * evaluation registers its ±3 HU "CT Number" limit under
 * `uniformity.center_HU` while the trend reads `materials.water.mean`: the same
 * number (ct_ge_540.py sets both from rect1mean of the water slice). Without
 * the alias that trend had no limits at all -- no limit lines on the chart,
 * a drift projection that could never fire, and an outlier detector running
 * its limit-less fallback rule.
 *
 * Why aliases here rather than renaming the evaluation's limitKey: the
 * limitKey path is not just a label. It is how per-scanner / per-model custom
 * limits in `routine.customValues` are addressed (e.g.
 * `{"<scannerId>": {series: {generic_data: {uniformity: {center_HU: {low, high}}}}}}`),
 * and it names the evaluated row in the test display and PDF report.
 * Re-keying the GE 540 evaluation to `materials.water.mean` would silently
 * detach any custom limit stored under `center_HU` and change reports, and
 * registering a second NumEval would show "CT Number" twice. Resolving the
 * lookup here changes no stored data or display; the exact key still wins
 * when it exists, so phantoms that already register the trend key (GE Big,
 * Siemens, Canon, ...) are unaffected. Used by both the server
 * (trendsHelpers.findLimits, evaluation chart) and the client
 * (trends/helpers.findLimitsForPhantomTestData, Trends page).
 */
export const TREND_LIMIT_KEY_ALIASES: { [trendLimitKey: string]: string[] } = {
  "series.generic_data.materials.water.mean": [
    // GE Optima 540 (server/src/modules/testStudy/evaluationLogic/ct_ge_optima_540.ts)
    "series.generic_data.uniformity.center_HU",
    // CT ACR registers image-level keys without the path prefix (ct_acr.ts)
    "water",
  ],
};

/** limitKeys to look for, most specific first, for a selected trend key */
export function candidateLimitKeys(selectedTrendKey: string[]): string[] {
  const key = ["series", "generic_data", ...selectedTrendKey].join(".");
  return [key, ...(TREND_LIMIT_KEY_ALIASES[key] || [])];
}
